import React, { createContext, useContext, useReducer, useEffect } from 'react';

// État initial du panier
const initialState = {
  items: [],
  total: 0,
  activePromotion: null, // null | 'promo1_livraison' | 'promo2_emporter'
  promotionDiscount: 0,
  promotionDetails: null
};

// Actions du panier
const cartActions = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART'
};

// Fonction pour détecter la taille de la pizza à partir du nom/options
const getPizzaSize = (item) => {
  if (!item.category || !item.category.includes('PIZZA')) return null;
  
  // Vérifier dans les options si disponible
  if (item.options && item.options.pizzaSize) {
    return item.options.pizzaSize;
  }
  
  // Vérifier dans le nom de l'item
  const name = item.name.toLowerCase();
  if (name.includes('junior')) return 'Junior';
  if (name.includes('senior')) return 'Senior';
  if (name.includes('mega') || name.includes('famille')) return 'Mega';
  
  return null;
};

// Fonction pour détecter quelle promotion s'applique et calculer la réduction
const detectPromotion = (items) => {
  // Vérifier si l'utilisateur a explicitement choisi une promo via le modal
  const forcedPromo = items.find(item => item.forcePromoType);
  const forcedPromoType = forcedPromo?.forcePromoType;
  
  // Filtrer uniquement les pizzas avec taille (Senior ou Mega)
  const pizzas = items.filter(item => {
    const size = getPizzaSize(item);
    return size === 'Senior' || size === 'Mega';
  });

  if (pizzas.length === 0) {
    return {
      activePromotion: null,
      promotionDiscount: 0,
      promotionDetails: null
    };
  }

  // Compter les pizzas par taille
  let seniorCount = 0;
  let megaCount = 0;
  const pizzasBySize = { Senior: [], Mega: [] };
  
  // Pour promo3, exclure Classica
  const pizzasExcludingClassica = pizzas.filter(pizza => 
    !pizza.name.toLowerCase().includes('classica') && 
    !pizza.originalName?.toLowerCase().includes('classica')
  );

  pizzas.forEach(pizza => {
    const size = getPizzaSize(pizza);
    const quantity = pizza.quantity || 1;
    
    if (size === 'Senior') {
      seniorCount += quantity;
      for (let i = 0; i < quantity; i++) {
        pizzasBySize.Senior.push(pizza);
      }
    } else if (size === 'Mega') {
      megaCount += quantity;
      for (let i = 0; i < quantity; i++) {
        pizzasBySize.Mega.push(pizza);
      }
    }
  });

  const totalPizzaCount = seniorCount + megaCount;

  // Tableau pour stocker toutes les promotions applicables
  const applicablePromotions = [];

  // PROMOTION 1 : 2 pizzas achetées = 3ème offerte (Livraison, hors Classica)
  // Condition : Au moins 3 pizzas Senior ou Mega (hors Classica)
  const totalPizzaCountNoClassica = pizzasExcludingClassica.length;
  
  if (totalPizzaCountNoClassica >= 3) {
    // Trouver la pizza la moins chère (hors Classica)
    const pizzaPrices = pizzasExcludingClassica.map(p => {
      const cleanPrice = p.price.toString().replace(',', '.').replace(/[^\d.]/g, '');
      return parseFloat(cleanPrice) || 0;
    });
    
    const minPrice = Math.min(...pizzaPrices);
    const freeCount = Math.floor(totalPizzaCountNoClassica / 3);
    const discount = minPrice * freeCount;

    applicablePromotions.push({
      activePromotion: 'promo1_livraison',
      promotionDiscount: discount,
      promotionDetails: {
        type: 'promo1',
        message: `${freeCount} pizza(s) offerte(s) (la moins chère, hors Classica)`,
        requiredMode: 'livraison',
        pizzaCount: totalPizzaCountNoClassica
      }
    });
  }

  // PROMOTION 2 : 2ème pizza à 2€ (Senior) ou 4€ (Mega) - À emporter
  // Condition : Au moins 2 pizzas de même catégorie (hors Classica)
  // Exclure Classica
  const seniorNoClassicaPromo2 = pizzasBySize.Senior.filter(p => 
    !p.name.toLowerCase().includes('classica') && 
    !p.originalName?.toLowerCase().includes('classica')
  );
  const megaNoClassicaPromo2 = pizzasBySize.Mega.filter(p => 
    !p.name.toLowerCase().includes('classica') && 
    !p.originalName?.toLowerCase().includes('classica')
  );
  
  if (seniorNoClassicaPromo2.length >= 2 || megaNoClassicaPromo2.length >= 2) {
    let discount = 0;
    let details = [];

    // Senior : la MOINS CHÈRE de chaque paire coûte 2€
    if (seniorNoClassicaPromo2.length >= 2) {
      const seniorPrices = seniorNoClassicaPromo2.map(p => {
        const cleanPrice = p.price.toString().replace(',', '.').replace(/[^\d.]/g, '');
        return parseFloat(cleanPrice) || 0;
      }).sort((a, b) => a - b); // Trier par prix croissant
      
      // Pour chaque paire, la MOINS CHÈRE (première après tri) coûte 2€
      const pairCount = Math.floor(seniorNoClassicaPromo2.length / 2);
      for (let i = 0; i < pairCount; i++) {
        const cheapestPizzaOriginalPrice = seniorPrices[i * 2]; // LA MOINS CHÈRE
        discount += Math.max(0, cheapestPizzaOriginalPrice - 2);
      }
      details.push(`${pairCount} Senior à 2€`);
    }

    // Mega : la MOINS CHÈRE de chaque paire coûte 4€
    if (megaNoClassicaPromo2.length >= 2) {
      const megaPrices = megaNoClassicaPromo2.map(p => {
        const cleanPrice = p.price.toString().replace(',', '.').replace(/[^\d.]/g, '');
        return parseFloat(cleanPrice) || 0;
      }).sort((a, b) => a - b);
      
      // Pour chaque paire, la MOINS CHÈRE (première après tri) coûte 4€
      const pairCount = Math.floor(megaNoClassicaPromo2.length / 2);
      for (let i = 0; i < pairCount; i++) {
        const cheapestPizzaOriginalPrice = megaPrices[i * 2]; // LA MOINS CHÈRE
        discount += Math.max(0, cheapestPizzaOriginalPrice - 4);
      }
      details.push(`${pairCount} Mega à 4€`);
    }

    applicablePromotions.push({
      activePromotion: 'promo2_emporter',
      promotionDiscount: discount,
      promotionDetails: {
        type: 'promo2',
        message: `2ème pizza : ${details.join(', ')} (hors Classica)`,
        requiredMode: 'emporter',
        seniorCount: seniorNoClassicaPromo2.length,
        megaCount: megaNoClassicaPromo2.length
      }
    });
  }

  // PROMOTION 3 : 1 pizza achetée = 2ème à -50% (À emporter, hors Classica)
  // Condition : Au moins 2 pizzas Senior ou Mega (hors Classica)
  if (pizzasExcludingClassica.length >= 2) {
    // Séparer par taille et exclure Classica
    const seniorNoClassica = [];
    const megaNoClassica = [];
    
    pizzasExcludingClassica.forEach(pizza => {
      const size = getPizzaSize(pizza);
      const quantity = pizza.quantity || 1;
      
      if (size === 'Senior') {
        for (let i = 0; i < quantity; i++) {
          seniorNoClassica.push(pizza);
        }
      } else if (size === 'Mega') {
        for (let i = 0; i < quantity; i++) {
          megaNoClassica.push(pizza);
        }
      }
    });

    let discount = 0;
    let details = [];

    // Senior : 2ème à -50%
    if (seniorNoClassica.length >= 2) {
      const seniorPrices = seniorNoClassica.map(p => {
        const cleanPrice = p.price.toString().replace(',', '.').replace(/[^\d.]/g, '');
        return parseFloat(cleanPrice) || 0;
      }).sort((a, b) => a - b); // Trier par prix croissant pour appliquer -50% sur la moins chère
      
      const pairCount = Math.floor(seniorNoClassica.length / 2);
      for (let i = 0; i < pairCount; i++) {
        const cheapestPrice = seniorPrices[i * 2]; // La moins chère de la paire
        discount += cheapestPrice * 0.5;
      }
      details.push(`${pairCount} Senior à -50%`);
    }

    // Mega : 2ème à -50%
    if (megaNoClassica.length >= 2) {
      const megaPrices = megaNoClassica.map(p => {
        const cleanPrice = p.price.toString().replace(',', '.').replace(/[^\d.]/g, '');
        return parseFloat(cleanPrice) || 0;
      }).sort((a, b) => a - b);
      
      const pairCount = Math.floor(megaNoClassica.length / 2);
      for (let i = 0; i < pairCount; i++) {
        const cheapestPrice = megaPrices[i * 2]; // La moins chère de la paire
        discount += cheapestPrice * 0.5;
      }
      details.push(`${pairCount} Mega à -50%`);
    }

    if (discount > 0) {
      applicablePromotions.push({
        activePromotion: 'promo3_emporter_50',
        promotionDiscount: discount,
        promotionDetails: {
          type: 'promo3',
          message: `2ème pizza : ${details.join(', ')} (hors Classica)`,
          requiredMode: 'emporter_ou_livraison',
          seniorCount: seniorNoClassica.length,
          megaCount: megaNoClassica.length
        }
      });
    }
  }

  // Si l'utilisateur a forcé une promo spécifique, utiliser uniquement celle-ci
  if (forcedPromoType) {
    const forcedPromoResult = applicablePromotions.find(p => p.activePromotion === forcedPromoType);
    if (forcedPromoResult) {
      return forcedPromoResult;
    }
  }

  // Choisir la meilleure promotion (celle qui donne le plus de réduction)
  if (applicablePromotions.length === 0) {
    return {
      activePromotion: null,
      promotionDiscount: 0,
      promotionDetails: null
    };
  }

  // Trier les promotions par réduction décroissante et retourner la meilleure
  applicablePromotions.sort((a, b) => b.promotionDiscount - a.promotionDiscount);
  return applicablePromotions[0];
};

// Fonction pour calculer le total avec promotion
const calculateTotalWithPromotion = (items) => {
  // Calculer le total de base
  const baseTotal = items.reduce((total, item) => {
    const cleanPrice = item.price.toString().replace(',', '.').replace(/[^\d.]/g, '');
    const price = parseFloat(cleanPrice) || 0;
    return total + (price * item.quantity);
  }, 0);

  // Détecter et appliquer la promotion
  const promoResult = detectPromotion(items);
  
  return {
    baseTotal,
    total: Math.max(0, baseTotal - promoResult.promotionDiscount),
    ...promoResult
  };
};

// Reducer pour gérer les actions du panier
const cartReducer = (state, action) => {
  switch (action.type) {
    case cartActions.ADD_ITEM:
      const existingItemIndex = state.items.findIndex(
        item => item.id === action.payload.id
      );
      
      let updatedItems;
      if (existingItemIndex >= 0) {
        updatedItems = state.items.map((item, index) =>
          index === existingItemIndex 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        updatedItems = [...state.items, { ...action.payload, quantity: 1 }];
      }
      
      const resultAdd = calculateTotalWithPromotion(updatedItems);
      
      return {
        items: updatedItems,
        total: resultAdd.total,
        activePromotion: resultAdd.activePromotion,
        promotionDiscount: resultAdd.promotionDiscount,
        promotionDetails: resultAdd.promotionDetails
      };

    case cartActions.REMOVE_ITEM:
      const filteredItems = state.items.filter(item => item.id !== action.payload);
      const resultRemove = calculateTotalWithPromotion(filteredItems);
      
      return {
        items: filteredItems,
        total: resultRemove.total,
        activePromotion: resultRemove.activePromotion,
        promotionDiscount: resultRemove.promotionDiscount,
        promotionDetails: resultRemove.promotionDetails
      };

    case cartActions.UPDATE_QUANTITY:
      const updatedItemsQuantity = state.items.map(item =>
        item.id === action.payload.id 
          ? { ...item, quantity: Math.max(0, action.payload.quantity) }
          : item
      ).filter(item => item.quantity > 0);
      
      const resultUpdate = calculateTotalWithPromotion(updatedItemsQuantity);
      
      return {
        items: updatedItemsQuantity,
        total: resultUpdate.total,
        activePromotion: resultUpdate.activePromotion,
        promotionDiscount: resultUpdate.promotionDiscount,
        promotionDetails: resultUpdate.promotionDetails
      };

    case cartActions.CLEAR_CART:
      return initialState;

    case cartActions.LOAD_CART:
      const loadedState = action.payload || initialState;
      const resultLoad = calculateTotalWithPromotion(loadedState.items);
      return {
        ...loadedState,
        total: resultLoad.total,
        activePromotion: resultLoad.activePromotion,
        promotionDiscount: resultLoad.promotionDiscount,
        promotionDetails: resultLoad.promotionDetails
      };

    default:
      return state;
  }
};

// Contexte du panier
const CartContext = createContext();

// Provider du panier
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Charger le panier depuis localStorage au démarrage
  useEffect(() => {
    const savedCart = localStorage.getItem('perfectoCart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        dispatch({ type: cartActions.LOAD_CART, payload: parsedCart });
      } catch (error) {
        console.error('Erreur lors du chargement du panier:', error);
      }
    }
  }, []);

  // Sauvegarder le panier dans localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('perfectoCart', JSON.stringify(state));
  }, [state]);

  // Actions du panier
  const addItem = (item) => {
    dispatch({ type: cartActions.ADD_ITEM, payload: item });
  };

  const removeItem = (itemId) => {
    dispatch({ type: cartActions.REMOVE_ITEM, payload: itemId });
  };

  const updateQuantity = (itemId, quantity) => {
    dispatch({ type: cartActions.UPDATE_QUANTITY, payload: { id: itemId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: cartActions.CLEAR_CART });
  };

  const getItemCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      ...state,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      getItemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart doit être utilisé dans un CartProvider');
  }
  return context;
};

export default CartContext;
